import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { ordersApi } from '../../services/api';
import { Eye, Package, Printer, X } from 'lucide-react';

const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await ordersApi.getAll();
      setOrders(data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order.order_id === orderId ? { ...order, status: newStatus } : order
      ));
      alert('Sipariş durumu güncellendi!');
    } catch (error) {
      alert('Durum güncellenirken hata oluştu!');
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'confirmed', label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
    { value: 'shipped', label: 'Kargoda', color: 'bg-purple-100 text-purple-700' },
    { value: 'delivered', label: 'Teslim Edildi', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: 'İptal', color: 'bg-red-100 text-red-700' }
  ];

  const getStatusColor = (status) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    return statusOptions.find(s => s.value === status)?.label || status;
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Sipariş Yönetimi</h1>
          <p className="text-gray-600 mt-2">Tüm siparişleri görüntüleyin ve yönetin</p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Package size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Henüz Sipariş Yok</h2>
            <p className="text-gray-600">Müşterilerinizden gelen siparişler burada görünecek.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Sipariş No</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Müşteri</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Ürün Sayısı</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Toplam</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Durum</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Tarih</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.order_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{order.order_id}</td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{order.customer_info.first_name} {order.customer_info.last_name}</p>
                          <p className="text-sm text-gray-600">{order.customer_info.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{order.items.length} ürün</td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-red-600">
                          {order.total.toLocaleString('tr-TR')} TL
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)} border-0 cursor-pointer`}
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Detayları Gör"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Order Details Modal - Enhanced */}
        {showModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Sipariş Detayları</h2>
                    <p className="text-sm text-gray-600 mt-1">#{selectedOrder.order_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrint}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Yazdır"
                    >
                      <Printer size={20} />
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Kapat"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Order Info Card */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Package size={18} />
                        Sipariş Bilgileri
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sipariş No:</span>
                          <span className="font-mono font-semibold">{selectedOrder.order_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tarih:</span>
                          <span className="font-medium">{new Date(selectedOrder.created_at).toLocaleString('tr-TR')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Durum:</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(selectedOrder.status)}`}>
                            {getStatusLabel(selectedOrder.status)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ödeme:</span>
                          <span className="font-medium capitalize">{selectedOrder.payment_method}</span>
                        </div>
                      </div>
                    </div>

                    {/* Customer Info Card */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-blue-900 mb-3">Müşteri Bilgileri</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="font-semibold text-blue-900">
                            {selectedOrder.customer_info.first_name} {selectedOrder.customer_info.last_name}
                          </p>
                        </div>
                        <div>
                          <p className="text-blue-800">
                            📧 {selectedOrder.customer_info.email}
                          </p>
                        </div>
                        <div>
                          <p className="text-blue-800">
                            📞 {selectedOrder.customer_info.phone}
                          </p>
                        </div>
                        <div className="pt-2 border-t border-blue-200">
                          <p className="text-blue-800">
                            📍 {selectedOrder.customer_info.address}
                          </p>
                          <p className="text-blue-800 mt-1">
                            {selectedOrder.customer_info.city}
                            {selectedOrder.customer_info.zip_code && ` - ${selectedOrder.customer_info.zip_code}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products Table */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Sipariş Ürünleri ({selectedOrder.items.length} ürün)</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Ürün</th>
                            <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Adet</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700 text-sm">Birim Fiyat</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700 text-sm">Toplam</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.items.map((item, index) => (
                            <tr key={index} className="border-t hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <p className="font-medium text-gray-800">{item.product_name}</p>
                                <p className="text-xs text-gray-500">ID: {item.product_id}</p>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="inline-block bg-gray-200 px-3 py-1 rounded-full text-sm font-semibold">
                                  {item.quantity}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right text-gray-700">
                                {item.price.toLocaleString('tr-TR')} TL
                              </td>
                              <td className="py-3 px-4 text-right font-semibold text-red-600">
                                {item.subtotal.toLocaleString('tr-TR')} TL
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t-2">
                          <tr>
                            <td colSpan="3" className="py-4 px-4 text-right font-bold text-gray-800 text-lg">
                              Genel Toplam:
                            </td>
                            <td className="py-4 px-4 text-right font-bold text-red-600 text-xl">
                              {selectedOrder.total.toLocaleString('tr-TR')} TL
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Status Update Section */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      🔄 Sipariş Durumu Güncelle
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {statusOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            handleStatusChange(selectedOrder.order_id, option.value);
                            setSelectedOrder({...selectedOrder, status: option.value});
                          }}
                          className={`py-3 px-3 rounded-lg text-sm font-semibold transition-all ${
                            selectedOrder.status === option.value
                              ? option.color + ' ring-2 ring-offset-2 ring-red-500 shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                      💡 İpucu: "İptal" durumuna geçildiğinde stok otomatik olarak geri eklenir.
                    </p>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="mt-6 pt-4 border-t flex gap-4">
                  <button
                    onClick={handlePrint}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Printer size={18} />
                    Yazdır
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;